import type { PersistedContextEntry, StorageAdapter } from '@cowbell/moo';

const SELECTED_AGENT_KEY = 'moo.v2.selectedAgentId';
const PERSISTED_CONTEXTS_KEY = 'moo.v2.persistedContexts';

/** Storage adapter for Moo V2.
 *
 *  - **Selected agent** lives in `sessionStorage` — per-tab, V1 parity
 *    (V1 stores the selected agent under the same per-tab semantics so
 *    two windows can pin two different agents without crosstalk).
 *  - **Persisted user contexts** live in `localStorage` — origin-wide,
 *    V2 enhancement (contexts the user pinned should survive tab
 *    restarts; the chat shell is the only place we surface them).
 *
 *  All reads / writes are wrapped — `localStorage` and `sessionStorage`
 *  can throw under Safari private mode, quota errors, or when storage
 *  is disabled by group policy. Failures degrade silently so the chat
 *  keeps working without persistence. */
export function makeCowbellStorageAdapter(): StorageAdapter {
  return {
    getSelectedAgentId(): string | null {
      try {
        return sessionStorage.getItem(SELECTED_AGENT_KEY);
      } catch {
        return null;
      }
    },
    setSelectedAgentId(id: string | null): void {
      try {
        if (id === null) {
          sessionStorage.removeItem(SELECTED_AGENT_KEY);
        } else {
          sessionStorage.setItem(SELECTED_AGENT_KEY, id);
        }
      } catch {
        // disabled / quota — degrade silently
      }
    },
    getPersistedContexts(): PersistedContextEntry[] | null {
      try {
        const raw = localStorage.getItem(PERSISTED_CONTEXTS_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as unknown;
        if (!Array.isArray(parsed)) return null;
        return parsed as PersistedContextEntry[];
      } catch {
        return null;
      }
    },
    setPersistedContexts(entries: PersistedContextEntry[]): void {
      try {
        if (entries.length === 0) {
          localStorage.removeItem(PERSISTED_CONTEXTS_KEY);
        } else {
          localStorage.setItem(PERSISTED_CONTEXTS_KEY, JSON.stringify(entries));
        }
      } catch {
        // disabled / quota — degrade silently
      }
    },
  };
}
